import { useAuthContext } from "providers/Auth";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Show } from "shared/components";
import { Card } from "shared/components/Card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { GetUnreadCountAPIResponse } from "stream-chat";
import { useChatContext } from "stream-chat-react";
import { Attachment01, Bell01, BellOff01 } from "untitledui-js-base";

const AsyncChannelCardPreview = ({ channelId }: { channelId: string }) => {
  const { client } = useChatContext();
  const [type, id] = channelId.split(":");
  // replaceall non-numeric characters
  const applicantId = id.replaceAll(/\D/g, "");

  const channel = client.getChannelById(type, id, {
    watch: true
  });

  const [canRead, setCanRead] = useState(false);

  useEffect(() => {
    channel.watch().then(() => {
      setCanRead(true);
    });
  }, []);

  if (!canRead) return <p>Loading...</p>;

  const lastMessage = channel.state.messages[channel.state.messages.length - 1];

  return (
    <Link
      to={`/applications/${channel.data?.application_id}/reports?applicantId=${applicantId}&report=chat`}
    >
      <div className="w-full p-1 hover:bg-alpha-40">
        {/* Channel name and unread count and last message preview */}
        <div className="flex items-center justify-between">
          <p className="text-base font-medium truncate">
            {channel.data?.name || channel.id}
          </p>
          <p className="text-xs p-0.5 px-3 rounded text-white bg-danger-500">
            {channel.state.unreadCount}
          </p>
        </div>

        {/* Message preview */}

        <p className="text-sm text-content-500 truncate mt-1 inline-flex items-center">
          {lastMessage.attachments?.length ? (
            <span className="font-medium text-content-300 italic inline-flex items-center gap-1 shrink-0 pr-1">
              <Attachment01 className="shrink-0 stroke-content-200" size="16" />
              [Attachment{lastMessage.attachments.length > 1 ? "s" : ""}:{" "}
              {lastMessage.attachments.length}]
            </span>
          ) : null}{" "}
          <span className="font-medium">
            {channel.state.messages[channel.state.messages.length - 1]?.text}
          </span>
        </p>
      </div>
    </Link>
  );
};

export const Notifications = () => {
  const [chime, setChime] = useState(false);
  const [unreadMessages, setUnreadMessages] =
    useState<GetUnreadCountAPIResponse>();
  const { client } = useChatContext();
  const { auth } = useAuthContext();

  const className =
    "relative text-sm font-medium flex gap-3 w-full whitespace-nowrap text-ellipsis overflow-hidden break-all items-center p-3 rounded-lg transition-all no-underline my-[2px] text-content-500 stroke-content-500 hover:bg-alpha-40";

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- lib has an error
    if (client) {
      const fetchUnread = () => {
        return client
          .getUnreadCount(auth?.stream_chat_id)
          .then((res) => {
            setUnreadMessages(res);
            setChime(Boolean(res.total_unread_count));
          })
          .catch(() => {
            // Do nothing
          });
      };

      fetchUnread();

      // chime on new messages

      client.on("notification.message_new", () => {
        fetchUnread();
      });

      client.on("message.new", () => {
        fetchUnread();
      });

      // remove chime is messages read and no other unread messages
      client.on("notification.message_read", () => {
        fetchUnread();
      });

      client.on("message.read", () => {
        fetchUnread();
      });
    }
  }, []);

  return (
    <Popover placement="top">
      <PopoverTrigger className="w-full">
        <div className={className}>
          <Bell01 className="shrink-0" size="20" stroke="inherit" />

          <p className="text-sm font-medium">Notifications</p>

          <Show if={chime}>
            <div className="absolute top-2.5 left-3 w-1.5 h-1.5 bg-danger-500 rounded-full animate-ping"></div>
          </Show>
        </div>
      </PopoverTrigger>
      <PopoverContent className=" z-[14]">
        <Card className="bg-white w-[340px] min-h-[340px] flex flex-col">
          <div className="flex items-center justify-between p-4 w-full border-b border-alpha-100">
            <h3 className="text-sm text-content-1000 font-medium">
              Notifications
            </h3>
          </div>
          <div className="px-1 py-2 space-y-1 h-full grow">
            {unreadMessages?.total_unread_count ? (
              <div>
                {unreadMessages.channels.map((channel) => {
                  return (
                    <AsyncChannelCardPreview
                      key={channel.channel_id}
                      channelId={channel.channel_id}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center flex-col flex-grow mt-4">
                <div className="p-5 rounded-full bg-alpha-40">
                  <BellOff01 size="40" className="stroke-content-400" />
                </div>
                <p className="text-sm text-content-1000 mt-2 font-medium">
                  No unread messages
                </p>
              </div>
            )}
          </div>
        </Card>
      </PopoverContent>
    </Popover>
  );
};
